<template>
  <div>
    <!-- Navbar -->
    <nav class="navbar navbar-expand-lg navbar-light bg-white fixed-top shadow-sm w-100">
      <div class="container-fluid px-4">
        <a class="navbar-brand fw-bold" href="#">
          <span class="text-success">Greenbelt</span>
          <span class="text-dark">Life Africa</span>
        </a>

        <!-- Toggler button -->
        <button
          class="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span class="navbar-toggler-icon"></span>
        </button>

        <!-- Nav links -->
        <div class="collapse navbar-collapse justify-content-end" id="navbarNav">
          <ul class="navbar-nav">
            <li class="nav-item">
              <a class="nav-link active" href="#home">Home</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#about">About Us</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#services">Services</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#pricing">Pricing</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#contact">Contact</a>
            </li>
            <li v-if="isLoggedIn" class="nav-item">
              <a class="nav-link" href="/dashboard">Dashboard</a>
            </li>
            <li v-else class="nav-item">
              <a class="nav-link btn btn-success text-white px-3 mx-2" href="/login">Client Login</a>
            </li>
          </ul>
        </div>
      </div>
    </nav>

    <!-- Hero Section -->
    <section id="home" class="hero-section text-white text-center py-5 mt-5">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-8 mx-auto">
            <h1 class="display-4 fw-bold mb-4">Transforming Waste Management in Africa</h1>
            <p class="lead mb-4">Professional, reliable, and eco-friendly garbage collection services. 
            We're committed to cleaner communities and a sustainable future.</p>
            <div class="d-flex flex-column flex-md-row justify-content-center gap-3">
              <a href="#contact" class="btn btn-light btn-lg px-4 py-3 fw-bold">
                <i class="bi bi-calendar-check me-2"></i>Schedule Pickup
              </a>
              <a href="#services" class="btn btn-outline-light btn-lg px-4 py-3">
                <i class="bi bi-info-circle me-2"></i>Our Services
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="bg-light py-5">
      <div class="container">
        <div class="row text-center">
          <div class="col-md-3">
            <h3 class="text-success fw-bold display-6">5000+</h3>
            <p class="text-muted">Happy Clients</p>
          </div>
          <div class="col-md-3">
            <h3 class="text-success fw-bold display-6">50+</h3>
            <p class="text-muted">Communities Served</p>
          </div>
          <div class="col-md-3">
            <h3 class="text-success fw-bold display-6">95%</h3>
            <p class="text-muted">Waste Recycled</p>
          </div>
          <div class="col-md-3">
            <h3 class="text-success fw-bold display-6">24/7</h3>
            <p class="text-muted">Customer Support</p>
          </div>
        </div>
      </div>
    </section>

    <!-- About Section -->
    <section id="about" class="py-5">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6">
            <h2 class="fw-bold text-success mb-4">About Greenbelt Life Africa</h2>
            <p class="mb-4">
              We are Africa's leading waste management solution, combining innovative technology 
              with dedicated service to revolutionize garbage collection across the continent.
            </p>
            <div class="d-flex mb-3">
              <i class="bi bi-check-circle-fill text-success me-3 fs-5"></i>
              <p class="mb-0">Eco-friendly waste disposal methods</p>
            </div>
            <div class="d-flex mb-3">
              <i class="bi bi-check-circle-fill text-success me-3 fs-5"></i>
              <p class="mb-0">Modern fleet of collection vehicles</p>
            </div>
            <div class="d-flex mb-3">
              <i class="bi bi-check-circle-fill text-success me-3 fs-5"></i>
              <p class="mb-0">Trained and professional staff</p>
            </div>
            <div class="d-flex">
              <i class="bi bi-check-circle-fill text-success me-3 fs-5"></i>
              <p class="mb-0">Community-focused approach</p>
            </div>
          </div>
          <div class="col-lg-6 mt-4 mt-lg-0">
            <img src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
                 alt="Waste Collection Team" class="img-fluid rounded shadow">
          </div>
        </div>
      </div>
    </section>

    <!-- Services Section -->
    <section id="services" class="py-5 bg-light">
      <div class="container">
        <h2 class="text-center fw-bold text-success mb-5">Our Comprehensive Services</h2>
        <div class="row g-4">
          <div class="col-md-4">
            <div class="card border-0 shadow-sm h-100 hover-shadow">
              <div class="card-body text-center p-4">
                <div class="service-icon mb-3">
                  <i class="bi bi-house-door-fill text-success fs-1"></i>
                </div>
                <h5 class="fw-bold">Residential Collection</h5>
                <p>Regular scheduled waste pickup for homes, apartments, and residential complexes.</p>
                <ul class="list-unstyled text-start">
                  <li><i class="bi bi-check text-success me-2"></i>Weekly/Monthly plans</li>
                  <li><i class="bi bi-check text-success me-2"></i>Separate recycling bins</li>
                  <li><i class="bi bi-check text-success me-2"></i>Flexible scheduling</li>
                </ul>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card border-0 shadow-sm h-100 hover-shadow">
              <div class="card-body text-center p-4">
                <div class="service-icon mb-3">
                  <i class="bi bi-building text-success fs-1"></i>
                </div>
                <h5 class="fw-bold">Commercial Services</h5>
                <p>Tailored waste management solutions for businesses and commercial establishments.</p>
                <ul class="list-unstyled text-start">
                  <li><i class="bi bi-check text-success me-2"></i>Office complexes</li>
                  <li><i class="bi bi-check text-success me-2"></i>Shopping centers</li>
                  <li><i class="bi bi-check text-success me-2"></i>Industrial areas</li>
                </ul>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card border-0 shadow-sm h-100 hover-shadow">
              <div class="card-body text-center p-4">
                <div class="service-icon mb-3">
                  <i class="bi bi-recycle text-success fs-1"></i>
                </div>
                <h5 class="fw-bold">Recycling Programs</h5>
                <p>Advanced recycling solutions to promote sustainability and environmental protection.</p>
                <ul class="list-unstyled text-start">
                  <li><i class="bi bi-check text-success me-2"></i>Plastic recycling</li>
                  <li><i class="bi bi-check text-success me-2"></i>Paper & cardboard</li>
                  <li><i class="bi bi-check text-success me-2"></i>Organic waste processing</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="py-5">
      <div class="container">
        <h2 class="text-center fw-bold text-success mb-5">Affordable Pricing Plans</h2>
        <div class="row g-4">
          <div class="col-md-4">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body p-4 text-center">
                <h5 class="fw-bold text-success">Basic Plan</h5>
                <div class="price mb-3">
                  <span class="display-6 fw-bold">KES 800</span>
                  <span class="text-muted">/month</span>
                </div>
                <ul class="list-unstyled mb-4">
                  <li class="mb-2"><i class="bi bi-check text-success me-2"></i>Weekly collection</li>
                  <li class="mb-2"><i class="bi bi-check text-success me-2"></i>1 standard bin</li>
                  <li class="mb-2"><i class="bi bi-check text-success me-2"></i>Basic recycling</li>
                  <li class="mb-2"><i class="bi bi-x text-danger me-2"></i>Bulk waste removal</li>
                </ul>
                <a href="#contact" class="btn btn-outline-success w-100">Choose Plan</a>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card border-success border-2 shadow-sm h-100">
              <div class="card-body p-4 text-center">
                <span class="badge bg-success position-absolute top-0 start-50 translate-middle">Most Popular</span>
                <h5 class="fw-bold text-success">Premium Plan</h5>
                <div class="price mb-3">
                  <span class="display-6 fw-bold">KES 1,500</span>
                  <span class="text-muted">/month</span>
                </div>
                <ul class="list-unstyled mb-4">
                  <li class="mb-2"><i class="bi bi-check text-success me-2"></i>Twice weekly collection</li>
                  <li class="mb-2"><i class="bi bi-check text-success me-2"></i>2 standard bins</li>
                  <li class="mb-2"><i class="bi bi-check text-success me-2"></i>Advanced recycling</li>
                  <li class="mb-2"><i class="bi bi-check text-success me-2"></i>Monthly bulk waste</li>
                </ul>
                <a href="#contact" class="btn btn-success w-100">Choose Plan</a>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body p-4 text-center">
                <h5 class="fw-bold text-success">Commercial Plan</h5>
                <div class="price mb-3">
                  <span class="display-6 fw-bold">KES 3,000</span>
                  <span class="text-muted">/month</span>
                </div>
                <ul class="list-unstyled mb-4">
                  <li class="mb-2"><i class="bi bi-check text-success me-2"></i>Daily collection</li>
                  <li class="mb-2"><i class="bi bi-check text-success me-2"></i>Custom bin sizes</li>
                  <li class="mb-2"><i class="bi bi-check text-success me-2"></i>Full recycling</li>
                  <li class="mb-2"><i class="bi bi-check text-success me-2"></i>Priority service</li>
                </ul>
                <a href="#contact" class="btn btn-outline-success w-100">Contact Sales</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section id="contact" class="py-5 bg-light">
      <div class="container">
        <div class="row">
          <div class="col-lg-6 mb-4 mb-lg-0">
            <h2 class="fw-bold text-success mb-4">Get Started Today</h2>
            <p class="mb-4">Join thousands of satisfied customers who trust Greenbelt Life Africa for their waste management needs.</p>
            
            <div class="contact-info mb-4">
              <div class="d-flex align-items-center mb-3">
                <i class="bi bi-telephone-fill text-success me-3 fs-4"></i>
                <div>
                  <h6 class="fw-bold mb-1">Call Us</h6>
                  <p class="mb-0">+254 700 000 000</p>
                </div>
              </div>
              <div class="d-flex align-items-center mb-3">
                <i class="bi bi-envelope-fill text-success me-3 fs-4"></i>
                <div>
                  <h6 class="fw-bold mb-1">Email Us</h6>
                  <p class="mb-0">info@greenbeltlifeafrica.com</p>
                </div>
              </div>
              <div class="d-flex align-items-center">
                <i class="bi bi-clock-fill text-success me-3 fs-4"></i>
                <div>
                  <h6 class="fw-bold mb-1">Working Hours</h6>
                  <p class="mb-0">Mon - Sat: 6:00 AM - 8:00 PM</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-lg-6">
            <div class="card shadow-sm border-0">
              <div class="card-body p-4">
                <h4 class="mb-4">Sign Up for Service</h4>
                <form @submit.prevent="createClient">
                  <div class="mb-3">
                    <label for="client_name" class="form-label">Full Name</label>
                    <input type="text" class="form-control" id="client_name" v-model="formData.client_name" required>
                  </div>
                  <div class="mb-3">
                    <label for="phone" class="form-label">Phone Number</label>
                    <input type="tel" class="form-control" id="phone" v-model="formData.phone" required>
                  </div>
                  <div class="mb-3">
                    <label for="email" class="form-label">Email Address</label>
                    <input type="email" class="form-control" id="email" v-model="formData.email">
                  </div>
                  <div class="mb-3">
                    <label for="area" class="form-label">Service Area</label>
                    <select class="form-select" v-model="formData.area" required>
                      <option value="" disabled>Select your area</option>
                      <option value="nairobi_cbd">Nairobi CBD</option>
                      <option value="westlands">Westlands</option>
                      <option value="karen">Karen</option>
                      <option value="ruaka">Ruaka</option>
                      <option value="other">Other Areas</option>
                    </select>
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Preferred Plan</label>
                    <div class="d-flex gap-2 flex-wrap">
                      <div class="form-check">
                        <input class="form-check-input" type="radio" v-model="formData.plan" value="basic">
                        <label class="form-check-label">Basic</label>
                      </div>
                      <div class="form-check">
                        <input class="form-check-input" type="radio" v-model="formData.plan" value="premium">
                        <label class="form-check-label">Premium</label>
                      </div>
                      <div class="form-check">
                        <input class="form-check-input" type="radio" v-model="formData.plan" value="commercial">
                        <label class="form-check-label">Commercial</label>
                      </div>
                    </div>
                  </div>
                  <button type="submit" class="btn btn-success w-100 py-3 fw-bold">
                    <i class="bi bi-send-check me-2"></i>Request Service
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-dark text-white py-5">
      <div class="container">
        <div class="row">
          <div class="col-lg-4 mb-4">
            <h4 class="fw-bold mb-3">
              <span class="text-success">Greenbelt</span>
              <span>Life Africa</span>
            </h4>
            <p>Leading waste management solutions for a cleaner, greener Africa.</p>
            <div class="social-icons mt-3">
              <a href="#" class="text-white me-3"><i class="bi bi-facebook fs-5"></i></a>
              <a href="#" class="text-white me-3"><i class="bi bi-twitter fs-5"></i></a>
              <a href="#" class="text-white me-3"><i class="bi bi-instagram fs-5"></i></a>
              <a href="#" class="text-white"><i class="bi bi-linkedin fs-5"></i></a>
            </div>
          </div>
          <div class="col-lg-2 col-md-6 mb-4">
            <h5 class="fw-bold mb-3">Quick Links</h5>
            <ul class="list-unstyled">
              <li class="mb-2"><a href="#home" class="text-white-50 text-decoration-none">Home</a></li>
              <li class="mb-2"><a href="#about" class="text-white-50 text-decoration-none">About Us</a></li>
              <li class="mb-2"><a href="#services" class="text-white-50 text-decoration-none">Services</a></li>
              <li><a href="#contact" class="text-white-50 text-decoration-none">Contact</a></li>
            </ul>
          </div>
          <div class="col-lg-3 col-md-6 mb-4">
            <h5 class="fw-bold mb-3">Services</h5>
            <ul class="list-unstyled">
              <li class="mb-2"><a href="#services" class="text-white-50 text-decoration-none">Residential Collection</a></li>
              <li class="mb-2"><a href="#services" class="text-white-50 text-decoration-none">Commercial Services</a></li>
              <li class="mb-2"><a href="#services" class="text-white-50 text-decoration-none">Recycling Programs</a></li>
              <li><a href="#services" class="text-white-50 text-decoration-none">Bulk Waste Removal</a></li>
            </ul>
          </div>
          <div class="col-lg-3 mb-4">
            <h5 class="fw-bold mb-3">Contact Info</h5>
            <p class="mb-2"><i class="bi bi-geo-alt-fill text-success me-2"></i> Nairobi, Kenya</p>
            <p class="mb-2"><i class="bi bi-telephone-fill text-success me-2"></i> +254 700 000 000</p>
            <p class="mb-0"><i class="bi bi-envelope-fill text-success me-2"></i> info@greenbeltlifeafrica.com</p>
          </div>
        </div>
        <hr class="text-white-50 my-4">
        <div class="text-center text-white-50">
          <small>&copy; 2025 Greenbelt Life Africa. All rights reserved.</small>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  computed: {
    isLoggedIn() {
      return this.$store.getters.isLoggedIn;
    }
  },
  
  methods: {
    handleLogout() {
      this.$store.dispatch('clearToken')
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      this.$router.push('/login')
    },
    
    async createClient() {
      try {
        const response = await axios.post("https://api.roberms.com/gla/new/client/registration/", this.formData)
        console.log(response)
        
        // Show success message
        alert('Thank you! Our customer service will contact you shortly.')
        
        // Reset form
        this.formData = {
          client_name: '',
          phone: '',
          email: '',
          area: '',
          plan: 'basic'
        }
        
      } catch (error) {
        console.error('Error submitting form:', error)
        alert('There was an error submitting your request. Please try again or call us directly.')
      }
    }
  },
  
  data() {
    return {
      user: "",
      formData: {
        client_name: '',
        phone: '',
        email: '',
        area: '',
        plan: 'basic'
      }
    }
  },
  
  mounted() {
    let user = localStorage.getItem('user')
    this.user = user ? JSON.parse(user) : null
    
    // Add Bootstrap icons
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css'
    document.head.appendChild(link)
    
    // Add custom CSS
    const style = document.createElement('style')
    style.textContent = `
      .hero-section {
        background: linear-gradient(rgba(0, 100, 0, 0.85), rgba(0, 100, 0, 0.9)), 
                    url('https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80');
        background-size: cover;
        background-position: center;
        padding: 100px 0;
      }
      
      .hover-shadow {
        transition: transform 0.3s ease, box-shadow 0.3s ease;
      }
      
      .hover-shadow:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 30px rgba(0,0,0,0.1) !important;
      }
      
      .service-icon {
        background: rgba(25, 135, 84, 0.1);
        width: 80px;
        height: 80px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 20px;
      }
      
      .nav-link.btn {
        padding: 8px 20px !important;
        border-radius: 5px;
      }
      
      .price {
        background: rgba(25, 135, 84, 0.1);
        padding: 20px;
        border-radius: 10px;
      }
    `
    document.head.appendChild(style)
  }
}
</script>